import useUser from "@/api/apis";
import {
  useCreateReview,
  useMyReview,
  useUpdateReview,
} from "@/components/compatibility/experiences/hooks/useReview";
import { useIsMobileView } from "@/contexts/ResponsiveProvider";
import { cn } from "@/libs/utils";
import { AuthUtils } from "@/utils/AuthUtils";
import { useTrans } from "@/utils/useTrans";
import { Button } from "@/v2-components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/v2-components/ui/dialog";
import { Form, FormField } from "@/v2-components/ui/form";
import ArrowLeftIcon from "@/v2-components/core/icon/arrow-left-icon";
import SuccessIcon from "@/v2-components/core/icon/success-icon";
import { TextArea } from "@/v2-components/ui/textarea";
import StarSlider from "@/v2-components/pages/games/detail/StarSlider";
import { SwitchCase } from "@/v2-components/switch-case";
import { yupResolver } from "@hookform/resolvers/yup";
import Image from "next/image";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { Trans } from "next-i18next";
import { useCreateContentComment } from "@/components/compatibility/experiences/hooks/useComments";
import { Checkbox } from "@/v2-components/ui/checkbox";
import { Label } from "@/v2-components/ui/label";
import { makeContactUsEmailContent } from "@/components/support/contactUs/email/utils";
import CrossIcon from "@/v2-components/core/icon/cross-icon";

type Phase = "write-opinion" | "wrote-opinion" | "rate" | "rated";

interface Props {
  slug: string;
  contentId: string;
  contentName: string;
  reviewCount: number;
  view: "write-opinion" | "rate";
  trigger?: React.ReactNode;
}

export default function ReviewAndOpinionDialog({
  reviewCount,
  contentName,
  contentId,
  slug,
  view,
  trigger,
}: Props) {
  const { t } = useTrans(["games"]);
  const router = useRouter();
  const [phase, setPhase] = useState<Phase>(view);
  const [open, setOpen] = useState(false);
  const isMobileView = useIsMobileView();
  const { user } = useUser();
  const myReview = useMyReview(contentId);

  const triggerText =
    view === "rate"
      ? myReview.data
        ? "Rerate My Experience"
        : "Rate My Experience"
      : "Write opinions";

  return (
    <Dialog
      onOpenChange={(open) => {
        if (open) {
          setPhase(view);
        }
        setOpen(open);
      }}
      open={open}
    >
      <DialogTrigger
        asChild
        onClick={(e) => {
          if (!user) {
            e.preventDefault();
            router.push(AuthUtils.getLoginUrl());
          }
        }}
      >
        {trigger ? (
          trigger
        ) : (
          <Button
            variant="darkgray-fill"
            shape="pill"
            size="sm"
            /**
             * 시안 `29889:101537` — `bg #21272a · r999 · px24 · 12/16 SemiBold white
             * · tracking .1`. 실측(1920)에서 **자간만** 달랐다(`normal`).
             *
             * ⚠️ 공용 `Button` 의 `size="sm"` 을 고치지 않는다 — 소비처가 전 리포에
             * 수백 곳이라 파급을 확인할 수 없다. 리포는 pill 버튼 자간을 호출처에서
             * 주는 관행이다(`HERO_CTA_BASE` 등).
             */
            className="z-10 px-6 tracking-[0.1px]"
          >
            {t(triggerText)}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent
        className={cn(
          "overflow-y-auto",
          phase === "rate" && "laptop:max-w-[640px]",
          phase === "rated" && "laptop:max-w-[400px]",
          phase === "write-opinion" && "laptop:max-w-[400px]",
          phase === "wrote-opinion" && "laptop:max-w-[400px]",
        )}
        fullScreen={isMobileView}
      >
        <DialogHeader className="flex min-h-16 items-center border-b border-coolgray-20 laptop:min-h-fit laptop:border-none">
          <DialogTitle
            className={cn(
              "sr-only laptop:not-sr-only",
              phase === "rate" ? "visible" : "invisible",
            )}
          >
            {t(
              phase === "rate"
                ? "Rate My Experience"
                : "We want to hear from you!",
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">
            We want to hear from you!
          </DialogDescription>
          <DialogClose className="hidden laptop:block" />
          <div
            className={cn(
              "flex flex-1 laptop:hidden",
              view !== phase && "justify-end",
            )}
          >
            <DialogClose asChild>
              <button>
                {view === phase ? (
                  <ArrowLeftIcon className="mx-4 h-6 w-6" />
                ) : (
                  <CrossIcon className="mx-4 h-6 w-6" />
                )}
              </button>
            </DialogClose>
          </div>
        </DialogHeader>

        <SwitchCase
          value={phase}
          caseBy={{
            rate: (
              <RatePhase
                slug={slug}
                contentId={contentId}
                setPhase={setPhase}
              />
            ),
            rated: <RatedPhase reviewCount={reviewCount} setPhase={setPhase} />,
            "write-opinion": (
              <WriteOpinionPhase
                contentName={contentName}
                contentId={contentId}
                setPhase={setPhase}
              />
            ),
            "wrote-opinion": <WroteOpinionPhase />,
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

/********************************************* Rate Phase **********************************************/
const ratingSchema = yup.object({
  "haptic-satisfaction": yup.number().min(0.5).max(5).required(),
  "haptic-quality": yup.number().min(0.5).max(5).required(),
  "connection-convenience": yup.number().min(0.5).max(5).required(),
});

type RatingSchema = yup.InferType<typeof ratingSchema>;

interface RatePhaseProps {
  slug: string;
  contentId: string;
  setPhase: (phase: Phase) => void;
}

function RatePhase({ slug, contentId, setPhase }: RatePhaseProps) {
  const { t } = useTrans(["games"]);
  const myReview = useMyReview(contentId);
  const createReview = useCreateReview(slug);
  const updateReview = useUpdateReview(slug);
  const { user } = useUser();

  const form = useForm({
    resolver: yupResolver(ratingSchema),
    defaultValues: {
      "connection-convenience": 0,
      "haptic-quality": 0,
      "haptic-satisfaction": 0,
    },
    mode: "onChange",
  });

  const submit = async (data: RatingSchema) => {
    if (!user) return null;

    try {
      myReview.data
        ? await updateReview.mutateAsync({
            ...myReview.data,
            contentId,
            hapticsSatisfactionRating: data["haptic-satisfaction"],
            connectionConvenienceRating: data["connection-convenience"],
            hapticsQualityRating: data["haptic-quality"],
            email: user?.email ?? "",
          })
        : await createReview.mutateAsync({
            contentId,
            hapticsSatisfactionRating: data["haptic-satisfaction"],
            connectionConvenienceRating: data["connection-convenience"],
            hapticsQualityRating: data["haptic-quality"],
            email: user?.email ?? "",
          });

      setPhase("rated");
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!myReview.data) return;
    form.setValue(
      "connection-convenience",
      myReview.data.connectionConvenienceRating,
    );
    form.setValue("haptic-quality", myReview.data.hapticsQualityRating);
    form.setValue(
      "haptic-satisfaction",
      myReview.data.hapticsSatisfactionRating,
    );
  }, [form, myReview.data]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(submit)}>
        <div className="flex flex-col gap-5 px-4 laptop:px-0">
          <h2 className="text-2xl">
            {t("Share your thoughts on the haptics and gameplay!")}
          </h2>
          <div className="flex flex-col items-center gap-4 rounded-lg border border-coolgray-20 px-4 py-6 laptop:flex-row laptop:justify-between laptop:gap-9">
            <div className="w-full space-y-2 text-base">
              <h3 className="font-500">{t("Haptic Satisfaction Rating")}</h3>
              <p>
                {t(
                  "How much more enjoyable was the game with haptics enabled?",
                )}
              </p>
            </div>
            <div className="flex select-none flex-col gap-2">
              <FormField
                control={form.control}
                name="haptic-satisfaction"
                render={({ field }) => (
                  <StarSlider
                    rating={field.value}
                    setRating={(value) => {
                      field.onChange(value);
                    }}
                  />
                )}
              />
              <div className="flex flex-1 justify-between text-[10px] text-coolgray-70">
                <p>{t("Significantly Unengaging")}</p>
                <p>{t("Highly Engaging")}</p>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center gap-4 rounded-lg border border-coolgray-20 px-4 py-6 laptop:flex-row laptop:justify-between laptop:gap-9">
            <div
              className="w-full space-y-2 text-base"
              id="haptic-quality-section"
            >
              <h3 className="font-500">{t("Haptic Quality Rating")}</h3>
              <p>
                {t("How would you rate the overall quality of the haptics?")}
              </p>
            </div>
            <div className="flex select-none flex-col gap-2">
              <FormField
                control={form.control}
                name="haptic-quality"
                render={({ field }) => (
                  <StarSlider
                    rating={field.value}
                    setRating={(value) => {
                      field.onChange(value);
                    }}
                  />
                )}
              />
              <div className="flex flex-1 justify-between text-[10px] text-coolgray-70">
                <p>{t("Lacking in Quality")}</p>
                <p>{t("Very Realistic")}</p>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center gap-4 rounded-lg border border-coolgray-20 px-4 py-6 laptop:flex-row laptop:justify-between laptop:gap-9">
            <div
              className="w-full space-y-2 text-base"
              id="connection-convenience-section"
            >
              <h3 className="font-500">{t("Connection Convenience Rating")}</h3>
              <p>
                {t(
                  "Was it easy to connect the game? (For mods, was the guide easy to follow?)",
                )}
              </p>
            </div>
            <div className="flex select-none flex-col gap-2">
              <FormField
                control={form.control}
                name="connection-convenience"
                render={({ field }) => (
                  <StarSlider
                    rating={field.value}
                    setRating={(value) => field.onChange(value)}
                  />
                )}
              />
              <div className="flex flex-1 justify-between text-[10px] text-coolgray-70">
                <p>{t("Very Difficult")}</p>
                <p>{t("Very Easy")}</p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="mb-16 justify-end gap-2 px-4 laptop:mb-0 laptop:px-0">
          <Button
            size="sm"
            variant="blue-fill"
            shape="pill"
            className="w-full px-6 laptop:w-auto"
            disabled={!form.formState.isValid || form.formState.isSubmitting}
          >
            {t("Submit")}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

/********************************************* Rated Phase **********************************************/
interface RatedPhaseProps {
  reviewCount: number;
  setPhase: (phase: Phase) => void;
}
function RatedPhase({ reviewCount, setPhase }: RatedPhaseProps) {
  const { t } = useTrans(["games"]);
  return (
    <div className="px-4 laptop:px-0">
      <div className="flex flex-col items-center text-center">
        <SuccessIcon className="mb-2 h-20 w-20 text-green-40" />
        <h2 className="text-4xl">
          <Trans
            t={t}
            i18nKey={`Review Submitted!`}
            values={{ reviewCount: reviewCount + 1 }}
          />
        </h2>
        <p className="mt-4 text-lg">
          {t(
            "Your feedback will be a great help to those considering a purchase!",
          )}
        </p>
      </div>

      <div className="mb-6 mt-10 flex flex-col items-center rounded-2xl bg-coolgray-10 px-4 py-6 text-center">
        <h3 className="mb-3 text-2xl">
          {t("Do you have any additional feedback?")}
        </h3>
        <p>
          {t(
            "Share your thoughts on haptics—your input helps us enhance realism!",
          )}
        </p>
        <Button
          variant="blue-fill"
          shape="pill"
          size="sm"
          className="mt-6 px-5"
          onClick={() => setPhase("write-opinion")}
        >
          {t("Leave your feedback on Haptics")}
        </Button>
      </div>
    </div>
  );
}

/********************************************* Write Opinion Phase **********************************************/
interface WriteOpinionPhaseProps {
  setPhase: (phase: Phase) => void;
  contentId: string;
  contentName: string;
}

const opinionSchema = yup.object({
  opinion: yup.string().max(1000).required(),
  needContact: yup.boolean(),
});

type OpinionSchema = yup.InferType<typeof opinionSchema>;

function WriteOpinionPhase({
  contentId,
  contentName,
  setPhase,
}: WriteOpinionPhaseProps) {
  const { user } = useUser();
  const { t } = useTrans(["games"]);
  const createContentComment = useCreateContentComment();

  const form = useForm({
    resolver: yupResolver(opinionSchema),
    defaultValues: { opinion: "", needContact: false },
    mode: "onChange",
  });

  const submit = async (data: OpinionSchema) => {
    if (!user) return;

    try {
      await createContentComment.mutateAsync({
        contentId,
        commentText: data.opinion,
        contentName: contentName,
        needContact: data.needContact ?? false,
      });
      if (data.needContact) {
        const CATEGORY = "game-software";
        const TOPIC = "compatible-games";

        const formData = new FormData();
        formData.append("emailTo", user.email);
        formData.append("topic", TOPIC);
        formData.append("category", CATEGORY);
        formData.append(
          "content",
          makeContactUsEmailContent({
            topic: TOPIC,
            gamename: contentName,
            category: CATEGORY,
            platform: "",
            email: user.email,
            name: user.userId,
            description: data.opinion,
            links: null,
          }),
        );

        await fetch("/api/support/send-ask/", {
          method: "POST",
          body: formData,
        });
      }

      setPhase("wrote-opinion");
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <form onSubmit={form.handleSubmit(submit)}>
      <div className="flex flex-col gap-5 px-4 laptop:px-0">
        <div className="flex flex-col gap-3">
          <h2 className="text-2xl">{t("We want to hear from you!")}</h2>
          <p className="text-base">
            {t(
              "Share your experience with bHaptic devices. Your feedback helps us with future updates and improvements.",
            )}
          </p>
        </div>
        <div className="flex flex-col gap-3">
          <TextArea
            placeholder={t(
              "Please share your thoughts on the haptic feedback for specific events or suggest any improvements you’d like to see.",
            )}
            className="min-h-[240px]"
            maxLength={1000}
            {...form.register("opinion")}
          />
          <p className="text-end text-base text-coolgray-40">{`${form.watch("opinion").length}/1000`}</p>
        </div>
      </div>

      <DialogFooter className="mb-16 flex-col justify-between gap-5 px-4 laptop:mb-0 laptop:flex-row laptop:items-center laptop:gap-2 laptop:px-0">
        <Label variant="checkbox" className="space-x-2">
          <Checkbox
            className="border-coolgray-50 data-[state=checked]:border-coolgray-90"
            {...form.register("needContact")}
            onCheckedChange={(check) => {
              form.setValue("needContact", check as boolean);
            }}
          />
          <span>{t("Contact me about my feedback")}</span>
        </Label>
        <Button
          size="sm"
          variant="blue-fill"
          shape="pill"
          className="w-full px-6 laptop:w-auto"
          disabled={!form.formState.isValid || form.formState.isSubmitting}
        >
          {t("Submit")}
        </Button>
      </DialogFooter>
    </form>
  );
}

/********************************************* Wrote Opinion Phase **********************************************/
function WroteOpinionPhase() {
  const { t } = useTrans(["games"]);
  return (
    <div className="px-4 laptop:px-0">
      <div className="flex flex-col items-center text-center">
        <div className="relative aspect-square w-[230px]">
          <Image
            src="/images/buzz.png"
            alt="thank you"
            fill
            className="absolute object-cover"
          />
        </div>
        <h2 className="text-4xl">
          {t("Thank you for sharing your valuable feedback!")}
        </h2>
        <p className="mt-4 text-lg">
          {t(
            "We will do our best to reflect your suggestions and make improvements.",
          )}
        </p>
      </div>

      <DialogFooter className="mb-16 justify-end gap-2 laptop:mb-0 laptop:px-0">
        <DialogClose asChild>
          <Button
            size="sm"
            variant="blue-fill"
            shape="pill"
            className="w-full px-7 laptop:w-auto"
          >
            {t("OK")}
          </Button>
        </DialogClose>
      </DialogFooter>
    </div>
  );
}
